import { setError as setNotificationError } from "../../redux/notificationReducer";
import { store } from "../../redux/store";
import {
  changeLoadingState,
  setAdminLogin,
  setAdminLogout,
  setAdminProfile,
} from "./adminReducer";
import { ApiEndpoints, httpClient } from "../../core";
import { setToken } from "../../utils/auth";

const { dispatch } = store;

type ApiResp<T = any> = {
  success: boolean;
  message: string;
  result?: T;
  results?: T;
};

type AdminLoginResult = {
  token: string;
  user?: {
    id: string;
    username: string;
    email: string;
    role: string;
  };
};

type AdminProfile = {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  createdAt: string;
  updatedAt: string;
};

const toMessage = (e: unknown, fallback = "Something went wrong") =>
  (e as any)?.response?.data?.message ||
  (e as any)?.data?.message ||
  (e as Error)?.message ||
  fallback;

export const adminLoginService = async (
  email: string,
  password: string
): Promise<boolean> => {
  try {
    dispatch(changeLoadingState(true));
    const resp = await httpClient().post<ApiResp<AdminLoginResult>>(
      ApiEndpoints.adminLogin,
      { email, password }
    );
    if (!resp.data.success || resp.status >= 400) {
      throw new Error(resp.data.message);
    }
    const token = resp.data.result?.token;
    if (!token) {
      throw new Error("Token missing in response");
    }
    setToken(token); // Store in cookie
    dispatch(setAdminLogin(token));
    return true;
  } catch (error) {
    dispatch(setNotificationError(toMessage(error, "Login failed")));
    return false;
  } finally {
    dispatch(changeLoadingState(false));
  }
};

type GoogleLoginPayload = { credential: string };

export const adminGoogleLogin = async (
  payload: GoogleLoginPayload
): Promise<ApiResp<AdminLoginResult>> => {
  try {
    dispatch(changeLoadingState(true));
    const resp = await httpClient().patch<ApiResp<AdminLoginResult>>(
      ApiEndpoints.adminGoogleLogin,
      payload
    );
    if (!resp.data.success || resp.status >= 400) {
      throw new Error(resp.data.message);
    }
    const token = resp.data.result?.token;
    if (token) {
      setToken(token); // Store in cookie
      dispatch(setAdminLogin(token));
    }
    return resp.data;
  } catch (error) {
    const msg = toMessage(error, "Google login failed");
    dispatch(setNotificationError(msg));
    throw new Error(msg);
  } finally {
    dispatch(changeLoadingState(false));
  }
};

export const forgetAdminPasswordService = async (
  username: string
): Promise<boolean> => {
  try {
    dispatch(changeLoadingState(true));
    const resp = await httpClient().post<ApiResp<{ ok: boolean }>>(
      `${ApiEndpoints.admin}/forget-password`,
      { username }
    );
    if (!resp.data.success || resp.status >= 400) {
      throw new Error(resp.data.message);
    }
    return true;
  } catch (error) {
    dispatch(
      setNotificationError(toMessage(error, "Failed to send reset link"))
    );
    return false;
  } finally {
    dispatch(changeLoadingState(false));
  }
};

export const resetAdminPasswordService = async (
  token: string,
  password: string
): Promise<boolean> => {
  try {
    dispatch(changeLoadingState(true));
    const resp = await httpClient().patch<ApiResp<{ ok: boolean }>>(
      `${ApiEndpoints.adminResetPassword}`,
      { token, password }
    );
    if (!resp.data.success || resp.status >= 400) {
      throw new Error(resp.data.message);
    }
    return true;
  } catch (error) {
    dispatch(setNotificationError(toMessage(error, "Reset failed")));
    return false;
  } finally {
    dispatch(changeLoadingState(false));
  }
};

export const getAdminProfile = async (): Promise<boolean> => {
  try {
    dispatch(changeLoadingState(true));
    const resp = await httpClient().get<ApiResp<AdminProfile>>(
      `${ApiEndpoints.admin}/profile`
    );
    if (!resp.data.success || resp.status >= 400) {
      throw new Error(resp.data.message);
    }
    const profile = resp.data.results ?? resp.data.result;
    if (profile) {
      dispatch(setAdminProfile(profile as AdminProfile));
    }
    return true;
  } catch (error) {
    dispatch(setNotificationError(toMessage(error, "Failed to load profile")));
    return false;
  } finally {
    dispatch(changeLoadingState(false));
  }
};

export const logoutAdmin = async (): Promise<boolean> => {
  try {
    dispatch(changeLoadingState(true));
    // Optionally notify API: await httpClient().post(`${ApiEndpoints.admin}/logout`);
    dispatch(setAdminLogout());
    return true;
  } catch (error) {
    dispatch(setNotificationError(toMessage(error, "Logout failed")));
    return false;
  } finally {
    dispatch(changeLoadingState(false));
  }
};
